import React from 'react';

import { cn } from '@/lib/utils';

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  help?: string;
  error?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, label, help, error, id, checked, disabled, ...props }, ref) => {
    const generatedId = React.useId();
    const helpId = React.useId();
    const errorId = React.useId();
    const switchId = id || generatedId;
    const isOn = Boolean(checked);

    return (
      <div className="flex w-full flex-col">
        <div className={cn('flex w-full items-center', label ? 'gap-3' : undefined)}>
          <label
            htmlFor={switchId}
            data-state={isOn ? 'on' : 'off'}
            className={cn(
              'flex items-center',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
              !label && 'min-h-[2.75rem] w-full justify-end px-4 py-3',
              className,
            )}
          >
            <input
              type="checkbox"
              className="peer sr-only"
              id={switchId}
              ref={ref}
              checked={checked}
              disabled={disabled}
              aria-describedby={error ? errorId : help ? helpId : undefined}
              aria-invalid={!!error}
              {...props}
            />
            <span
              data-testid="switch-track"
              className={cn(
                'pointer-events-none relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border-2 border-transparent transition-colors',
                isOn ? 'bg-neutral-900' : 'bg-neutral-200',
                disabled && !isOn && 'bg-neutral-100',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform',
                  isOn ? 'translate-x-5' : 'translate-x-0.5',
                )}
              />
            </span>
          </label>
          {label ? (
            <label
              htmlFor={switchId}
              className={cn(
                'text-sm font-medium leading-none',
                disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
              )}
            >
              {label}
            </label>
          ) : null}
        </div>
        {help && !error && (
          <p id={helpId} className="ml-14 text-sm text-muted-foreground">
            {help}
          </p>
        )}
        {error && (
          <p id={errorId} className="ml-14 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    );
  }
);
Switch.displayName = "Switch";

export { Switch };
